import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErr } from "@/lib/format";
import { toast } from "sonner";
import { EnvelopeIcon, PaperPlaneTiltIcon, SpinnerIcon, CheckIcon, ClockCounterClockwiseIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-HN", { dateStyle: "short", timeStyle: "short" });
  } catch { return String(iso).slice(0, 16); }
};

/**
 * Historial de envíos por correo de un documento (bitácora 0.7):
 * fecha, destinatario(s) y usuario que envió.
 * items: [{to: [..], subject, sent_at, sent_by_email, sent_by_name, auto}]
 */
export function EmailHistory({ items, title = "Historial de envíos por correo", testid = "email-history" }) {
  const list = [...(items || [])].sort((a, b) => String(b.sent_at || "").localeCompare(String(a.sent_at || "")));
  return (
    <div className="mt-4 border border-border/60 rounded-md" data-testid={testid}>
      <div className="px-3 py-2 border-b border-border/60 text-[10px] uppercase tracking-wider text-text-muted flex items-center gap-1.5">
        <ClockCounterClockwiseIcon size={12} /> {title} ({list.length})
      </div>
      {list.length === 0 ? (
        <div className="px-3 py-2 text-xs text-text-muted">Aún no se ha enviado por correo.</div>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase text-text-muted">
              <th className="px-3 py-1.5">Fecha</th><th className="px-3 py-1.5">Destinatario</th><th className="px-3 py-1.5">Enviado por</th>
            </tr>
          </thead>
          <tbody>
            {list.map((h, i) => (
              <tr key={i} className="border-t border-border/30">
                <td className="px-3 py-1.5 whitespace-nowrap">{fmtDT(h.sent_at)}</td>
                <td className="px-3 py-1.5">{(Array.isArray(h.to) ? h.to : [h.to]).filter(Boolean).join(", ")}</td>
                <td className="px-3 py-1.5 text-text-secondary">
                  {h.sent_by_name || h.sent_by_email || "—"}
                  {h.auto && <span className="ml-1 text-[9px] uppercase bg-agro-green/10 text-agro-green border border-agro-green/30 rounded px-1">auto</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Modal genérico para enviar un documento por correo.
 * endpoint: ruta POST (p.ej. `/credit-notes/${id}/send-email`)
 * extraBody: campos adicionales para el endpoint (p.ej. bultos de la guía)
 */
export function SendDocEmailModal({ title, docLabel, endpoint, defaultSubject, clienteId, defaultTo = "", extraBody = {}, onClose, onSent }) {
  const [emails, setEmails] = useState(defaultTo);
  const [subject, setSubject] = useState(defaultSubject || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (defaultTo || !clienteId) return;
    api.get(`/clients/${clienteId}`).then(({ data }) => { if (data?.email) setEmails(data.email); }).catch(() => {});
  }, [clienteId, defaultTo]);

  const submit = async (e) => {
    e.preventDefault();
    const list = emails.split(",").map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) { toast.error("Agrega al menos un correo destinatario"); return; }
    setSending(true);
    try {
      const { data } = await api.post(endpoint, { to: list, subject, message: message || undefined, ...extraBody });
      setSent(true);
      toast.success(`${docLabel} enviada a ${list.length} destinatario(s)`);
      if (onSent) onSent(data);
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setSending(false); }
  };

  const em = useModalForm({ value: { emails, subject, message }, onClose: onClose, onSave: submit, mensaje: "¿Está seguro de que desea enviar el correo?" });
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" {...em.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="send-doc-email-modal">
        <form onSubmit={em.guardar}>
          <div className="p-5 border-b border-border">
            <h3 className="font-heading text-lg flex items-center gap-2"><EnvelopeIcon size={20} className="text-agro-green" /> {title}</h3>
            <p className="text-xs text-text-muted mt-1">Se enviará el PDF adjunto y quedará registrado en la bitácora del documento.</p>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted font-medium mb-1.5 block">Destinatarios *</label>
              <input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="cliente@email.com, otro@email.com"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none" data-testid="doc-email-to" autoFocus />
              <div className="text-[10px] text-text-muted mt-1">Separa varios correos con coma.</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted font-medium mb-1.5 block">Asunto</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none" data-testid="doc-email-subject" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted font-medium mb-1.5 block">Mensaje adicional (opcional)</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none resize-none" />
            </div>
          </div>
          <div className="p-4 border-t border-border flex justify-end gap-2 bg-app/40">
            <button type="button" onClick={em.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary hover:bg-white/5">Cancelar</button>
            <button type="submit" disabled={(sending || sent) || em.saving} data-testid="doc-email-send"
              className="px-4 py-2 text-sm bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md disabled:opacity-50 flex items-center gap-1.5">
              {sending ? (<><SpinnerIcon size={14} className="animate-spin" /> Enviando…</>) :
                sent ? (<><CheckIcon size={14} weight="bold" /> Enviado</>) :
                (<><PaperPlaneTiltIcon size={14} weight="bold" /> Enviar</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
